/**
 * Centralized MFA Messages and Error Handling
 * Provides user-friendly, actionable messages for all MFA scenarios
 */

export const MFA_MESSAGES = {
  // Success Messages
  SUCCESS: {
    LOGIN_SUCCESS: 'Connexion réussie!',
    ENROLLMENT_AND_LOGIN: 'Configuration terminée! Vous êtes maintenant connecté.',
    ENROLLMENT_COMPLETE: 'Configuration terminée avec succès!',
    MFA_RESET: 'Authentification réinitialisée. Génération d\'un nouveau QR code...',
  },

  // Error Messages with Actions
  ERRORS: {
    INVALID_CODE: {
      title: 'Code incorrect',
      message: 'Le code saisi n\'est pas valide.',
      action: 'Vérifiez le code dans votre application ou attendez le prochain (30s).',
    },
    NETWORK_ERROR: {
      title: 'Erreur de connexion',
      message: 'Impossible de vérifier le code.',
      action: 'Vérifiez votre connexion internet et réessayez.',
    },
    SERVER_ERROR: {
      title: 'Erreur du serveur',
      message: 'Un problème technique est survenu.',
      action: 'Patientez quelques instants et réessayez.',
    },
    USER_DATA_ERROR: {
      title: 'Erreur des données utilisateur',
      message: 'Impossible de récupérer vos informations de profil.',
      action: 'Reconnectez-vous ou contactez le support.',
    },
    QR_CODE_ERROR: {
      title: 'Erreur du QR Code',
      message: 'Impossible de générer le QR Code.',
      action: 'Actualisez la page et réessayez la configuration.',
    },
    ENROLLMENT_ERROR: {
      title: 'Erreur de configuration',
      message: 'Impossible d\'initialiser l\'authentification.',
      action: 'Reconnectez-vous et relancez la configuration.',
    },
    INCOMPLETE_CODE: 'Veuillez saisir les 6 chiffres',
    RESET_FAILED: 'Erreur lors de la réinitialisation',
  },

  // Informational Messages
  INFO: {
    TOTP_GUIDANCE: {
      message: 'Les codes à 6 chiffres se renouvellent automatiquement toutes les 30 secondes dans votre application d\'authentification.',
    },
    ENROLLMENT_GUIDE: {
      message: 'Scannez le QR Code avec Google Authenticator, Microsoft Authenticator, Authy ou toute autre application TOTP compatible.',
    },
    FIRST_TIME_SETUP: {
      message: 'L\'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.',
    },
  },
};

/**
   * Get error message based on error response
   */
export const getErrorMessage = (error) => {
  // Check for specific error codes from backend
  const errorCode = error?.response?.data?.code;
  const statusCode = error?.response?.status;

  // Handle specific error codes
  switch (errorCode) {
    case 'INVALID_TOTP_CODE':
    case 'TOTP_VERIFICATION_FAILED':
    case 'EXPIRED_TOTP_CODE':
    case 'CODE_EXPIRED':
      return MFA_MESSAGES.ERRORS.INVALID_CODE;

    case 'MFA_NOT_ENABLED':
    case 'MFA_NOT_CONFIGURED':
      return {
        title: 'Authentification Multi-Facteurs non configurée',
        message: 'L\'authentification à deux facteurs n\'est pas encore configurée.',
        action: 'Configurez d\'abord votre Authentification Multi-Facteurs.',
      };

    default:
      break;
  }

  // Handle HTTP status codes
  switch (statusCode) {
    case 400:
    case 401:
      return MFA_MESSAGES.ERRORS.INVALID_CODE;
    case 404:
      return {
        title: 'Service non trouvé',
        message: 'Le service d\'authentification est temporairement indisponible.',
        action: 'Contactez le support technique.',
      };
    case 429:
      return {
        title: 'Trop de tentatives',
        message: 'Vous avez fait trop de tentatives.',
        action: 'Attendez quelques minutes avant de réessayer.',
      };
    case 500:
    case 502:
    case 503:
      return MFA_MESSAGES.ERRORS.SERVER_ERROR;
    default:
      break;
  }

  // Check for network errors
  if (!error?.response) {
    return MFA_MESSAGES.ERRORS.NETWORK_ERROR;
  }

  // Fallback to invalid code error
  return MFA_MESSAGES.ERRORS.INVALID_CODE;
};

/**
   * Show enhanced toast message with title and action
   */
export const showEnhancedToast = (toast, messageObj, type = 'error') => {
  if (typeof messageObj === 'string') {
    toast[type](messageObj);
    return;
  }

  const { title, message, action } = messageObj;

  const fullMessage = action
    ? `${title}\n${message}\nAction: ${action}`
    : `${title}\n${message}`;

  toast[type](fullMessage, {
    autoClose: type === 'error' ? 8000 : 5000, // Longer for errors
    closeOnClick: true,
  });
};
